import { useShallow } from 'zustand/react/shallow';
import { useQuery } from '@tanstack/react-query';
import { usePrivy } from '@privy-io/react-auth';

import { DiscountStatus } from '@/types';
import { getPrivyPrimaryAddress } from '@/hooks';
import { cookieAwarePublicV2Api } from '@/lib/api-clients/rest-client';
import { useAccountStore } from '@/store/use-account-store';
import {
  Discount,
  DiscountGroup,
  isDiscountActive,
} from '@/store/use-capital-store';
import { privateV2Api } from '@/lib/api-clients/rest-client';
import {
  CapitalPlan,
  useCapitalPlans,
} from '@/features/vest-capital/api/get-capital-plans';

interface RewardDTO {
  code_id: string;
  code: string;
  discount_percent: number;
  expires_at: string;
  redeemed_at?: string;
  status: DiscountStatus;
  applicable_products: {
    name: string;
    price_id: string;
  }[];
}

interface SignupRefDiscountDTO {
  source: string;
  code_id: string;
  code: string;
  discount_percent: number;
  valid: boolean;
  max_redemptions: number;
  times_redeemed: number;
  expires_at?: string;
  first_purchase_only?: boolean;
  applicable_products: {
    name: string;
    price_id: string;
  }[];
}

interface GetRewardsResponse {
  rewards: RewardDTO[];
  signup_ref_discount?: SignupRefDiscountDTO | null;
  next_referral_discount: number;
}

type DiscountDTO = {
  code_id: string;
  code: string;
  discount_percent: number;
  expires_at: string;
  redeemed_at?: string | null;
  status: DiscountStatus;
  applicable_products: {
    name: string;
    price_id: string;
  }[];
};

const getSignupRefDiscountStatus = (
  discount: SignupRefDiscountDTO,
): DiscountStatus => {
  return discount.valid &&
    (discount.times_redeemed < discount.max_redemptions ||
      discount.max_redemptions === 0)
    ? DiscountStatus.ACTIVE
    : DiscountStatus.REVOKED;
};

const parseDiscountDTOToDiscountGroup = (
  discountDTO: DiscountDTO,
  capitalPlans: CapitalPlan[],
): DiscountGroup => {
  const hasPlans = capitalPlans.length > 0;

  const discounts = (discountDTO.applicable_products ?? [])
    .map((product) => {
      const capitalPlan = capitalPlans?.find(
        (plan) => plan.priceId === product.price_id,
      );
      const name = capitalPlan?.name ?? product.name;
      const planTitle = name.split('|')[1]?.trim() || name || '';

      return {
        codeId: discountDTO.code_id,
        code: discountDTO.code,
        name,
        planTitle,
        priceId: product.price_id,
        productId: capitalPlan?.productId,
        unitAmount: capitalPlan?.unitAmount,
        discountPct: discountDTO.discount_percent,
        redeemedAt: discountDTO.redeemed_at,
        expiresAt: discountDTO.expires_at,
        status: discountDTO.status,
        _hasMatchingPlan: !!capitalPlan,
      };
    })
    .filter((d) => isDiscountActive(d) && (!hasPlans || d._hasMatchingPlan))
    .map(({ _hasMatchingPlan: _, ...discount }) => discount as Discount)
    .sort(
      (a, b) =>
        (a.unitAmount ?? Number.POSITIVE_INFINITY) -
        (b.unitAmount ?? Number.POSITIVE_INFINITY),
    );

  const planTitles = Array.from(
    new Set(discounts.map((discount) => discount.planTitle).filter(Boolean)),
  );

  return {
    codeId: discountDTO.code_id,
    code: discountDTO.code,
    discountPct: discountDTO.discount_percent,
    expiresAt: discountDTO.expires_at,
    redeemedAt: discountDTO.redeemed_at,
    status: discountDTO.status,
    discounts,
    planTitles,
  };
};

const parseRewardDTOToDiscountGroup = (
  reward: RewardDTO,
  capitalPlans: CapitalPlan[],
): DiscountGroup => {
  return parseDiscountDTOToDiscountGroup(reward, capitalPlans);
};

const parseSignupRefDiscountDTOToDiscountGroup = (
  signupRefDiscount: SignupRefDiscountDTO,
  capitalPlans: CapitalPlan[],
): DiscountGroup => {
  return parseDiscountDTOToDiscountGroup(
    {
      ...signupRefDiscount,
      expires_at: signupRefDiscount.expires_at || '9999-12-31T23:59:59.999Z',
      redeemed_at: null,
      status: getSignupRefDiscountStatus(signupRefDiscount),
    },
    capitalPlans,
  );
};

const isUsableDiscountGroup = (group: DiscountGroup) => {
  return group.discounts.length > 0 && group.discounts.every(isDiscountActive);
};

async function getRewards(apiKey: string, capitalPlans: CapitalPlan[]) {
  return cookieAwarePublicV2Api
    .get<GetRewardsResponse>('/v2/referrals/rewards', {
      headers: {
        'X-API-KEY': apiKey,
      },
    })
    .then((response) => {
      const rewards = (response.rewards ?? []).map((reward) =>
        parseRewardDTOToDiscountGroup(reward, capitalPlans),
      );
      const signupRefDiscount = response.signup_ref_discount
        ? parseSignupRefDiscountDTOToDiscountGroup(
            response.signup_ref_discount,
            capitalPlans,
          )
        : null;
      const usableSignupRefDiscount =
        signupRefDiscount && isUsableDiscountGroup(signupRefDiscount)
          ? signupRefDiscount
          : null;

      return {
        rewards: usableSignupRefDiscount
          ? [...rewards, usableSignupRefDiscount]
          : rewards,
        signup_ref_discount: usableSignupRefDiscount,
        next_referral_discount: response.next_referral_discount,
      };
    });
}

const useRewards = () => {
  const { user } = usePrivy();
  const address = getPrivyPrimaryAddress(user);
  const { data: capitalPlans } = useCapitalPlans({
    queryConfig: {
      enabled: !!address,
    },
  });
  const { hasAuth, apiKey, capitalStatus } = useAccountStore(
    useShallow((state) => ({
      hasAuth: state.hasAuth,
      apiKey: state.getAuthParams(address, { skipFallback: true })?.apiKey,
      capitalStatus: state.getCapitalStatus(state.address),
    })),
  );

  return useQuery({
    enabled: hasAuth && !!address && !!apiKey && !!capitalPlans,
    queryKey: ['rewards', address, apiKey, capitalStatus],
    queryFn: () => getRewards(apiKey!, capitalPlans!),
  });
};

export default useRewards;
